import { useEffect, useState } from "react";
import { Clock, Gamepad2, Hourglass, Play, ShoppingBag, Timer, Wallet, X } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { Badge, Button, Card, cx, DurationInput, EmptyState, NumberStepper, PageLoader, SectionTitle, Textarea } from "@/components/ui";
import { Points, ProgressBar } from "@/components/bits";
import { formatRemaining, StopTimeButton, useSessionClock } from "@/components/ScreenTime";
import { api } from "@/lib/api";
import { KIND_LABELS, timeAgo } from "@/lib/format";
import {
  useAction,
  useBankEntries,
  useBanks,
  useKidSummary,
  useRedemptions,
  useRewardTypes,
  useStore,
} from "@/lib/queries";
import { useSession } from "@/lib/session";
import { formatUnits, isConvertible, pointsForUnits, unitsForPoints } from "@shared/economy";
import { formatDuration } from "@shared/duration";
import type { BankDTO, RedemptionDTO, RewardTypeDTO, StoreItemDTO } from "@shared/types";

function useCountdown(endsAt: number | null, maxSeconds = Infinity) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!endsAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (!endsAt || endsAt <= now) return null;
  // Clamp so a slightly fast/slow device clock never shows more than the session length.
  const s = Math.min(maxSeconds, Math.round((endsAt - now) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function ConvertSheet({ rt, available, onClose }: { rt: RewardTypeDTO; available: number; onClose: () => void }) {
  const { family } = useSession();
  const step = rt.kind === "cash" ? 1 : 15;
  const maxUnits = Math.floor(unitsForPoints(available, rt.pointsPerUnit!) / step) * step;
  const [units, setUnits] = useState(Math.min(maxUnits, step) || step);
  const cost = pointsForUnits(units, rt.pointsPerUnit!);
  const convert = useAction(() => api.post(`/rewards/${rt.id}/convert`, { units }), {
    success: rt.requiresApproval ? "Request sent to a parent!" : `${formatUnits(units, rt)} added! 🎉`,
    onSuccess: onClose,
  });
  const quick = (rt.kind === "cash" ? [1, 2, 5, 10, 20] : [15, 30, 60, 90, 120]).filter((u) => pointsForUnits(u, rt.pointsPerUnit!) <= available);
  return (
    <Sheet
      open
      onClose={onClose}
      title={rt.kind === "cash" ? "Cash out" : `Get ${rt.name}`}
      footer={
        <Button block size="lg" onClick={() => convert.mutate()} loading={convert.isPending} disabled={cost > available || units <= 0}>
          {rt.requiresApproval ? "Ask a parent" : "Convert"} · <Points value={cost} family={family} />
        </Button>
      }
    >
      <p className="text-sm text-muted">
        {rt.pointsPerUnit} {family.currencyName} = {formatUnits(1, rt)}. You have <Points value={available} family={family} /> to spend.
      </p>
      {quick.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {quick.map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => setUnits(u)}
              className={cx(
                "rounded-full px-4 py-2 font-extrabold transition",
                units === u ? "bg-brand text-on-brand" : "bg-sunken ring-1 ring-line",
              )}
            >
              {formatUnits(u, rt)}
            </button>
          ))}
        </div>
      )}
      <div className="mt-4">
        {rt.kind === "time" ? (
          <DurationInput value={units} onChange={setUnits} min={step} max={Math.max(step, maxUnits)} step={step} />
        ) : (
          <NumberStepper value={units} onChange={setUnits} min={step} max={Math.max(step, maxUnits)} step={step} suffix="$" />
        )}
      </div>
      {cost > available && <p className="mt-2 text-sm font-bold text-danger">Not enough {family.currencyName} yet.</p>}
      {rt.requiresApproval && <p className="mt-3 text-sm text-muted">A parent will approve this{rt.kind === "cash" ? " and hand you the cash" : ""}.</p>}
    </Sheet>
  );
}

function UseTimeSheet({ rt, bank, onClose }: { rt: RewardTypeDTO; bank: BankDTO; onClose: () => void }) {
  const caps = [bank.balance, bank.remainingThisWeek ?? Infinity, rt.dailyCap !== null ? Math.max(0, rt.dailyCap - bank.usedToday) : Infinity];
  const max = Math.floor(Math.min(...caps));
  const [minutes, setMinutes] = useState(Math.min(30, max) || 0);
  const use = useAction(() => api.post(`/rewards/${rt.id}/use`, { units: minutes }), {
    success: `Enjoy your ${formatDuration(minutes)}! ⏱️`,
    onSuccess: onClose,
  });
  const blocked = !bank.scheduleAllowedNow;
  return (
    <Sheet
      open
      onClose={onClose}
      title={`Use ${rt.name}`}
      footer={
        <Button block size="lg" variant="coin" onClick={() => use.mutate()} loading={use.isPending} disabled={blocked || minutes <= 0 || minutes > max}>
          <Timer className="size-5" /> Start {formatDuration(minutes)}
        </Button>
      }
    >
      {blocked ? (
        <p className="rounded-2xl bg-warn-soft p-3 font-bold text-warn">{bank.scheduleReason}</p>
      ) : max <= 0 ? (
        <p className="rounded-2xl bg-sunken p-3 font-bold text-muted">
          {bank.balance <= 0 ? "No time saved yet — convert some first." : "You've hit your limit for now."}
        </p>
      ) : (
        <>
          <p className="text-sm text-muted">You can use up to {formatDuration(max)} right now.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[15, 30, 45, 60, 90].filter((m) => m <= max).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMinutes(m)}
                className={cx("rounded-full px-4 py-2 font-extrabold", minutes === m ? "bg-brand text-on-brand" : "bg-sunken ring-1 ring-line")}
              >
                {formatDuration(m)}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <DurationInput value={minutes} onChange={setMinutes} min={Math.min(5, max)} max={max} step={5} />
          </div>
          <p className="mt-3 text-xs text-muted">
            {rt.integrationId ? "Your time will be sent to your console automatically." : "Show a parent the timer so they can start your time."}
          </p>
        </>
      )}
    </Sheet>
  );
}

function TimeCard({ rt, bank, available, receivedAt }: { rt: RewardTypeDTO; bank: BankDTO; available: number; receivedAt: number }) {
  const { user } = useSession();
  const [sheet, setSheet] = useState<"convert" | "use" | null>(null);
  // Time for a connected console is granted in chunks on the device; everything else runs as a countdown here.
  const device = !!rt.integrationId;
  const entries = useBankEntries(device ? user.id : undefined);
  const lastUse = entries.data?.entries.find((e) => e.rewardTypeId === rt.id && e.kind === "usage");
  const countdown = useCountdown(lastUse ? lastUse.createdAt + -lastUse.amount * 60_000 : null, lastUse ? -lastUse.amount * 60 : undefined);
  const left = useSessionClock(bank.session, receivedAt);
  const running = !!bank.session && left !== null && left > 0;
  const start = useAction(() => api.post<{ minutes: number }>(`/rewards/${rt.id}/start`), {
    success: (s) => `${formatDuration(s.minutes)} on the clock. Have fun! ⏱️`,
  });
  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="flex size-12 items-center justify-center rounded-2xl bg-sky-500/15 text-2xl">{rt.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="font-extrabold">{rt.name}</div>
          <div className="text-sm text-muted">{formatDuration(bank.balance)} saved</div>
        </div>
        <Gamepad2 className="size-5 text-faint" />
      </div>
      {device && countdown && (
        <div className="mt-3 flex items-center justify-between rounded-2xl bg-coin-soft px-4 py-3 text-coin-ink">
          <span className="flex items-center gap-2 font-extrabold">
            <Timer className="size-5" /> Time running
          </span>
          <span className="text-2xl font-black tabular-nums">{countdown}</span>
        </div>
      )}
      {running && bank.session && (
        <div
          role="timer"
          aria-label="Screen time left"
          className={cx(
            "mt-3 rounded-2xl px-4 py-3",
            left <= 5 * 60_000 ? "bg-warn-soft text-warn" : "bg-coin-soft text-coin-ink",
          )}
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 font-extrabold">
              <Timer className="size-5" /> Time running
            </span>
            <span className="text-2xl font-black tabular-nums">{formatRemaining(left)} left</span>
          </div>
          {left <= 5 * 60_000 && <p className="mt-1 text-sm font-bold">Almost done — start wrapping up.</p>}
        </div>
      )}
      {bank.weeklyCap !== null && (
        <div className="mt-3">
          <div className="mb-1 flex justify-between text-xs font-bold text-muted">
            <span>This week</span>
            <span>
              {formatDuration(bank.usedThisWeek)} of {formatDuration(bank.weeklyCap)}
            </span>
          </div>
          <ProgressBar value={bank.usedThisWeek / Math.max(1, bank.weeklyCap)} color={bank.usedThisWeek >= bank.weeklyCap ? "var(--danger)" : "var(--brand)"} />
        </div>
      )}
      {!bank.scheduleAllowedNow && !running && (
        <p className="mt-2 flex items-center gap-1.5 text-sm font-bold text-warn">
          <Clock className="size-4" /> {bank.scheduleReason}
        </p>
      )}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button variant="secondary" onClick={() => setSheet("convert")} disabled={available < (rt.pointsPerUnit ?? 1) * 5}>
          Get more
        </Button>
        {device ? (
          <Button variant="coin" onClick={() => setSheet("use")} disabled={bank.balance <= 0}>
            <Timer className="size-4" /> Use time
          </Button>
        ) : running && bank.session ? (
          <StopTimeButton session={bank.session} label="Stop" />
        ) : (
          <Button variant="coin" onClick={() => start.mutate()} loading={start.isPending} disabled={bank.balance < 1 || !bank.scheduleAllowedNow}>
            <Play className="size-4" /> Start
          </Button>
        )}
      </div>
      {running && <p className="mt-2 text-xs text-muted">Stopping saves the whole minutes left for later.</p>}
      {sheet === "convert" && <ConvertSheet rt={rt} available={available} onClose={() => setSheet(null)} />}
      {sheet === "use" && <UseTimeSheet rt={rt} bank={bank} onClose={() => setSheet(null)} />}
    </Card>
  );
}

function CashCard({ rt, available }: { rt: RewardTypeDTO; available: number }) {
  const [open, setOpen] = useState(false);
  const worth = unitsForPoints(Math.max(0, available), rt.pointsPerUnit!);
  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="flex size-12 items-center justify-center rounded-2xl bg-brand-soft text-2xl">{rt.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="font-extrabold">{rt.name}</div>
          <div className="text-sm text-muted">Your {formatUnits(worth, rt)} is ready to cash out</div>
        </div>
        <Wallet className="size-5 text-faint" />
      </div>
      <Button className="mt-3" block variant="secondary" onClick={() => setOpen(true)} disabled={worth < 1}>
        Cash out
      </Button>
      {open && <ConvertSheet rt={rt} available={available} onClose={() => setOpen(false)} />}
    </Card>
  );
}

function StoreItemCard({ item, available }: { item: StoreItemDTO; available: number }) {
  const { family } = useSession();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const affordable = available >= item.price;
  const soldOut = item.stock !== null && item.stock <= 0;
  const buy = useAction(() => api.post(`/store/${item.id}/redeem`, { note: note || undefined }), {
    success: "Request sent to a parent!",
    onSuccess: () => setOpen(false),
  });
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={soldOut}
        className={cx(
          "flex flex-col rounded-3xl bg-card p-3 text-left shadow-card ring-1 ring-line/60 transition active:scale-[0.98]",
          soldOut && "opacity-50",
        )}
      >
        <span className="flex h-20 items-center justify-center rounded-2xl bg-sunken text-5xl">{item.icon}</span>
        <span className="mt-2 line-clamp-2 min-h-[2.5rem] text-sm font-extrabold leading-tight">{item.name}</span>
        <span className="mt-1 flex items-center justify-between">
          <Points value={item.price} family={family} className={affordable ? "text-coin-ink" : "text-muted"} />
          {soldOut ? <Badge tone="danger">Sold out</Badge> : !affordable && <span className="text-xs font-bold text-faint">{Math.round((available / item.price) * 100)}%</span>}
        </span>
        {!affordable && !soldOut && <ProgressBar value={Math.max(0, available) / item.price} className="mt-1.5 h-1.5" color="var(--coin)" />}
      </button>
      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={`${item.icon} ${item.name}`}
        footer={
          <Button block size="lg" variant="coin" disabled={!affordable || soldOut} loading={buy.isPending} onClick={() => buy.mutate()}>
            {affordable ? (
              <>
                Ask to buy · <Points value={item.price} family={family} />
              </>
            ) : (
              `${item.price - Math.max(0, available)} more ${family.currencyName} to go`
            )}
          </Button>
        }
      >
        <Badge>{KIND_LABELS[item.category]}</Badge>
        {item.description && <p className="mt-3 text-muted">{item.description}</p>}
        {item.stock !== null && <p className="mt-2 text-sm font-bold text-muted">{item.stock} left</p>}
        {affordable && (
          <>
            <label className="mt-4 block text-sm font-bold">Note for your parent (optional)</label>
            <Textarea className="mt-1.5" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="Can we do it Saturday?" />
            <p className="mt-1 text-xs text-muted">Tip: no last names, addresses, phone numbers or school names.</p>
          </>
        )}
      </Sheet>
    </>
  );
}

function RequestRow({ r }: { r: RedemptionDTO }) {
  const { family } = useSession();
  const cancel = useAction(() => api.post(`/redemptions/${r.id}/cancel`), { success: "Request cancelled" });
  return (
    <Card className="flex items-center gap-3 p-3">
      <span className="flex size-10 items-center justify-center rounded-2xl bg-sunken">
        {r.status === "pending" ? <Hourglass className="size-5 text-warn" /> : <ShoppingBag className="size-5 text-brand" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate font-bold">{r.title}</div>
        <div className="text-xs text-muted">
          <Points value={r.points} family={family} /> · {timeAgo(r.createdAt)}
          {r.parentNote ? ` · “${r.parentNote}”` : ""}
        </div>
      </div>
      {r.status === "pending" ? (
        <button type="button" aria-label="Cancel request" onClick={() => cancel.mutate()} className="rounded-full p-2 text-muted hover:bg-sunken">
          <X className="size-5" />
        </button>
      ) : (
        <Badge tone={r.status === "approved" ? "coin" : r.status === "fulfilled" ? "brand" : "danger"}>
          {r.status === "approved" ? "Coming soon" : r.status === "fulfilled" ? "Delivered" : r.status === "denied" ? "Not now" : "Cancelled"}
        </Badge>
      )}
    </Card>
  );
}

export function KidShop() {
  const { user, family } = useSession();
  const summary = useKidSummary(user.id);
  const rewards = useRewardTypes();
  const banks = useBanks();
  const store = useStore();
  const requests = useRedemptions();

  if (!summary.data || !rewards.data || !banks.data || !store.data) return <PageLoader />;
  const available = summary.data.available;
  const convertible = rewards.data.rewardTypes.filter((t) => t.active && isConvertible(t));
  const recent = (requests.data?.redemptions ?? []).filter((r) => r.status !== "cancelled").slice(0, 8);

  return (
    <div>
      <PageHeader title="Shop" subtitle="Spend what you've earned" bell="/kid/notifications" />
      <Card className="mt-3 flex items-center justify-between bg-coin-soft ring-coin/30">
        <div>
          <div className="text-sm font-bold text-coin-ink">Ready to spend</div>
          <Points value={available} family={family} className="text-3xl text-coin-ink" />
        </div>
        {summary.data.pendingSpend > 0 && (
          <Badge tone="warn">
            <Hourglass className="size-3" /> {summary.data.pendingSpend} on hold
          </Badge>
        )}
      </Card>

      {convertible.length > 0 && (
        <>
          <SectionTitle>Turn {family.currencyName} into…</SectionTitle>
          <div className="grid gap-3 lg:grid-cols-2">
            {convertible.map((rt) => {
              if (rt.kind === "time") {
                const bank = banks.data.banks.find((b) => b.rewardTypeId === rt.id);
                return bank ? <TimeCard key={rt.id} rt={rt} bank={bank} available={available} receivedAt={banks.dataUpdatedAt} /> : null;
              }
              return <CashCard key={rt.id} rt={rt} available={available} />;
            })}
          </div>
        </>
      )}

      <SectionTitle>Family store</SectionTitle>
      {store.data.items.length === 0 ? (
        <EmptyState icon="🛍️" title="The store is empty">
          Ask a parent to add rewards — or add something to your Goals!
        </EmptyState>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {store.data.items.map((item) => (
            <StoreItemCard key={item.id} item={item} available={available} />
          ))}
        </div>
      )}

      {recent.length > 0 && (
        <>
          <SectionTitle>My requests</SectionTitle>
          <div className="space-y-2">
            {recent.map((r) => (
              <RequestRow key={r.id} r={r} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
